"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";

const faqs = [
  {
    q: "I want to migrate to Australia, but don't know where to begin. Can Hola World help?",
    a: "Yes, starting your migration to Australia can be confusing because there are many visa options and requirements. Hola World will help you check your eligibility, find the best visa for you, and guide you through the paperwork and application process. This way, you can start your move to Australia feeling clear and confident."
  },
  {
    q: "Which visa should I choose if I want to settle in Australia permanently?",
    a: "The best visa for you depends on your education, work experience, and long-term plans. Skilled migration visas such as subclass 189, 190, and 491 are common ways to get permanent residency. Hola World will look at your background and suggest the visa that fits you best for settling in Australia."
  },
  {
    q: "Is IELTS required for Australia migration or PR?",
    a: "Most Australian visas need you to show your English skills. Tests like IELTS or PTE are usually accepted for student visas, skilled migration, and permanent residency. The score you need depends on the visa. Hola World will help you pick the right test and get ready to reach your target score."
  },
  {
    q: "How long does the Australia migration process take?",
    a: "How long it takes depends on the type of visa and how strong your application is. Student visas might take a few weeks, but skilled migration or PR visas can take several months because of extra checks and waiting for invitations. Hola World will keep you updated at every step so you can plan with confidence."
  },
  {
    q: "Can I migrate to Australia without a job offer?",
    a: "Yes, some ways to migrate to Australia do not need a job offer, especially skilled visas that use a points system and depend on job demand. Having a job can still make your application stronger. Hola World will check your eligibility and help you pick the pathway with the best chance of approval."
  },
  {
    q: "Can I get Australia PR after studying there?",
    a: "Yes, studying in Australia can help you get permanent residency if you pick the right course and career path. Things like job demand and your work experience matter too. Hola World helps students plan their studies in a way that increases their chances of getting PR in Australia."
  }
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState(1);

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    <section className="py-20 bg-white px-4">
      <div className="container mx-auto max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Side - Heading + Image */}
          <div className="lg:sticky lg:top-24 space-y-8">
            {/* Heading */}
            <motion.h2
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="text-5xl md:text-6xl font-black leading-tight tracking-tight text-slate-900"
            >
              Answers<br />You Need
            </motion.h2>

            {/* Image with Perspective Effect */}
            <motion.div
              initial={{ opacity: 0, rotateX: 20 }}
              whileInView={{ opacity: 1, rotateX: 0 }}
              transition={{ duration: 0.6 }}
              className="w-full h-auto overflow-hidden cursor-pointer"
              style={{ perspective: "1200px" }}
              whileHover={{
                rotateX: 10,
                rotateY: 10,
              }}
            >
              <div
                style={{
                  transformStyle: "preserve-3d",
                  transition: "transform 0.3s ease-out",
                }}
              >
                <img
                  src="/assets/faq/faq-image.png"
                  alt="FAQ help center"
                  className="w-full h-auto object-contain"
                  onError={(e) => {
                    e.currentTarget.src = "/logo.png";
                  }}
                />
              </div>
            </motion.div>
          </div>

          {/* Right Side - FAQ Accordion */}
          <div className="space-y-0">
            {faqs.map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="border-b border-dashed border-slate-300 last:border-b-0"
              >
                {/* Trigger */}
                <div
                  className="w-full flex items-start justify-between gap-6 py-6 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => toggleAccordion(i)}
                >
                  <span className="text-base md:text-lg font-semibold text-slate-900 flex-1 leading-normal">
                    {faq.q}
                  </span>
                  <div className="text-2xl font-light text-slate-900 shrink-0 pt-1 select-none">
                    {openIndex === i ? "−" : "+"}
                  </div>
                </div>

                {/* Content */}
                {openIndex === i && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <p className="pb-6 text-slate-600 text-base leading-relaxed font-normal">
                      {faq.a}
                    </p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
